<template>
    <div class="infographic-canvas">
        <Transition name="image-transition">
            <div
                v-if="!image"
                ref="component"
                class="infographic-canvas__component"
            >
                <slot />
            </div>

            <img
                v-else
                class="infographic-canvas__image"
                :src="`/${image}`"
                :alt="alt"
            >
        </Transition>
    </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue'

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    alt: {
        type: String,
        required: true,
    },
})
const image = ref(null)
const component = ref(null)

async function checkIfImageExists() {
    try {
        return await $fetch(`/api/infographic-exists?name=${props.name}`)
    } catch {
        return { state: false, image: null }
    }
}

async function generateBase64Image() {
    try {
        const { default: html2canvas } = await import('html2canvas')

        const canvas = await html2canvas(component.value, {
            useCORS: true,
            allowTaint: true,
            scale: window.devicePixelRatio || 1,
            logging: false,
        })

        return canvas.toDataURL('image/png', 1)
    } catch (error) {
        console.error(error)
    }
}

async function uploadBase64Image(base64Image) {
    try {
        const data = await $fetch('/api/infographic', {
            method: 'POST',
            body: { base64: base64Image, name: props.name },
        })

        return data?.url || null
    } catch (error) {
        console.error(error)
        return null
    }
}

const imageExists = await checkIfImageExists()
if (imageExists.state) {
    image.value = imageExists.image.pathname
}

onMounted(() => {
    nextTick(async () => {
        if (image.value)
            return

        const base64Image = await generateBase64Image()
        image.value = await uploadBase64Image(base64Image)
    })
})
</script>

<style lang="scss">
.infographic-canvas {
    container: infographic-canvas / inline-size;
    position: relative;
    overflow: hidden;

    &__component,
    &__image {
        width: 100%;
    }
}

.image-transition-enter-active,
.image-transition-leave-active {
    transition:
        opacity 0.8s ease-in-out,
        transform 0s ease-in-out 1s;
    will-change: opacity, transform;
}

.image-transition-leave-active {
    position: absolute;
}

.image-transition-enter-from,
.image-transition-leave-to {
    opacity: 0;
}
</style>
